import { useEffect } from 'react';
import { useDrop } from 'react-dnd';

import { CustomEvents, ToolTypes } from '@labradorsports/constants';
import {
    applyOffset,
    usePlayEditor,
    useFieldViewport,
    useSelected,
    FieldPiece,
    flipPoint,
    FrameSnapshot,
    Move,
} from '@labradorsports/play-rendering';
import { arrayReplace } from '@labradorsports/utils';

import { useFieldContainer, usePlugins } from '../providers/index.js';

interface UsePieceDropProps {
    setTempFrames: (frames: FrameSnapshot[] | null) => void;
    setManipulatingPieces: (pieces: FieldPiece[]) => void;
}

export default function usePieceDrop({ setTempFrames, setManipulatingPieces }: UsePieceDropProps) {
    const { containerRef } = useFieldContainer();
    // We expect play to always be defined in this component
    const { PlayConfig, play, currentFrame, currentFrameIdx } = usePlayEditor();
    const { fieldViewport, zoomTransform } = useFieldViewport();

    const { setSelected } = useSelected();

    const { getDefaultMove, updatePiece } = usePlugins();

    const removeHover = () => {
        setTempFrames(null);
        setManipulatingPieces([]);
    };

    const setHover = (newHover: FieldPiece[]) => {
        const newFrame = currentFrame.copy();

        newHover.forEach((piece) => {
            if (piece.type === ToolTypes.BALL) {
                newFrame.balls = [...newFrame.balls, piece];
            } else if ([ToolTypes.BLUEPLAYER, ToolTypes.ORANGEPLAYER, ToolTypes.COACH].includes(piece.type)) {
                newFrame.players = [...newFrame.players, piece];
            } else if (
                [
                    ToolTypes.COMMENT,
                    ToolTypes.GOAL,
                    ToolTypes.CONE,
                    ToolTypes.OLINE,
                    ToolTypes.DLINE,
                    ToolTypes.RECTANGLE,
                    ToolTypes.ELLIPSE,
                ].includes(piece.type)
            ) {
                newFrame.pieces = [...(newFrame.pieces ?? []), piece];
            } else if (piece instanceof Move) {
                newFrame.moves = [...newFrame.moves, piece];
            }
        });

        setTempFrames(arrayReplace(play.frameSnapshots, currentFrameIdx, newFrame));
        setManipulatingPieces(newHover);
    };

    useEffect(() => {
        window.addEventListener(CustomEvents.TOOL_DRAG_END, removeHover);

        return () => {
            window.removeEventListener(CustomEvents.TOOL_DRAG_END, removeHover);
        };
    }, []);

    const calcOffset = (globalOffset: any) => {
        const elOffset = containerRef.current.getBoundingClientRect();

        return fieldViewport.getFieldCoords(
            applyOffset(
                {
                    x: globalOffset.x - elOffset.left,
                    y: globalOffset.y - elOffset.top,
                },
                flipPoint(zoomTransform)
            )
        );
    };

    const [dropProps, drop] = useDrop({
        accept: Object.keys(PlayConfig.ToolTypes),
        drop: (item: any, monitor: any) => {
            const offset = calcOffset(monitor.getClientOffset());

            updatePiece(item, offset);

            removeHover();
        },
        hover: (item: any, monitor: any) => {
            setSelected(null);

            const offset = calcOffset(monitor.getClientOffset());
            const newHover = getDefaultMove(item.type, offset);

            if (newHover) {
                setHover(Array.isArray(newHover) ? newHover : [newHover]);
            }
        },
        collect: (monitor: any) => ({
            isOver: monitor.isOver(),
        }),
    });

    useEffect(() => {
        if (!dropProps.isOver) {
            removeHover();
        }
    }, [dropProps.isOver]);

    return { drop };
}
